/**
 * Toasts for notifications arriving while the desktop is open (#1093),
 * bottom right above the taskbar — under the shell-free routes bottom left,
 * beside the icon rail (#1169). Each closes itself after a few seconds
 * (paused while hovered or focused); "Öffnen" goes where a click in the
 * panel would go. Reduced motion: no slide, the toast just appears.
 */
import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { Notification } from '@/services/notification-service'
import { NotificationIcon } from './NotificationRow'
import { notificationMeta, notificationPreview } from './notificationDisplay'
import { toastGeometry, type PanelAnchor } from './panelSize'
import { ACCENT, FOCUS_RING, INSTANT_TRANSITION, PANEL_TRANSITION, readable } from './notificationStyles'

export const TOAST_DURATION_MS = 6000

interface ToastProps {
  notification: Notification
  onOpen: (notification: Notification) => void
  onDismiss: (id: string) => void
}

function NotificationToast({ notification, onOpen, onDismiss }: ToastProps) {
  const { t, i18n } = useTranslation()
  const [paused, setPaused] = useState(false)
  const text = notificationPreview(t, notification, i18n.language)
  const meta = notificationMeta(notification, t)

  useEffect(() => {
    if (paused) return
    const timer = window.setTimeout(() => onDismiss(notification.id), TOAST_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [paused, notification.id, onDismiss])

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto flex items-start gap-3 w-[360px] max-w-[calc(100vw-32px)] px-3.5 py-3 rounded-[14px] border border-[var(--window-border-color)] bg-[var(--window-background)] shadow-2xl"
      style={{ backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
    >
      <NotificationIcon notification={notification} />
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-[var(--text-color)] line-clamp-2">{text.title}</span>
        {text.message && <span className="text-xs text-[var(--text-secondary)] line-clamp-2">{text.message}</span>}
        {meta && <span className="text-xs text-[var(--text-secondary)] opacity-80 truncate">{meta}</span>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={() => onOpen(notification)}
          className={`text-xs font-semibold whitespace-nowrap px-1.5 py-1 rounded-md hover:underline ${FOCUS_RING}`}
          style={{ color: readable(ACCENT) }}
        >
          {t('notificationCenter.actions.open')}
        </button>
        <button
          type="button"
          onClick={() => onDismiss(notification.id)}
          aria-label={t('notificationCenter.toast.dismiss')}
          title={t('notificationCenter.toast.dismiss')}
          className={`h-6 w-6 flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  )
}

interface NotificationToastsProps {
  toasts: Notification[]
  onOpen: (notification: Notification) => void
  onDismiss: (id: string) => void
  /** Same anchor as the panel: bottom right above the taskbar (default) or bottom left beside the rail. */
  anchor?: PanelAnchor
}

export function NotificationToasts({ toasts, onOpen, onDismiss, anchor = 'taskbar-right' }: NotificationToastsProps) {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  // Toasts slide in from the edge they sit at.
  const slideX = anchor === 'rail-left' ? -40 : 40
  return (
    <div
      role="region"
      aria-label={t('notificationCenter.toast.region')}
      data-anchor={anchor}
      className="fixed z-[15010] flex flex-col-reverse gap-2 pointer-events-none"
      style={toastGeometry(anchor)}
    >
      <div aria-live="polite" className="contents">
        <AnimatePresence initial={false}>
          {toasts.map((notification) => (
            <motion.div
              key={notification.id}
              layout={!reduceMotion}
              initial={reduceMotion ? false : { opacity: 0, x: slideX }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? { opacity: 0, transition: INSTANT_TRANSITION } : { opacity: 0, x: slideX }}
              transition={reduceMotion ? INSTANT_TRANSITION : PANEL_TRANSITION}
            >
              <NotificationToast notification={notification} onOpen={onOpen} onDismiss={onDismiss} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}
